import React, { useEffect, useMemo, useRef, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import { FiAlertCircle, FiCheck, FiPlus, FiSave } from "react-icons/fi";
import Modal from "../Modal/Modal";
import {
  SPREADSHEET_COLUMNS,
  annotateSpreadsheetGrid,
  buildSpreadsheetGrid,
  getSpreadsheetCellValue,
  isSpreadsheetRowBlank,
  normalizeSpreadsheetGrid,
  spreadsheetGridToChanges,
  validateSpreadsheetGrid,
} from "../../logic/spreadsheetEditorValidation";
import "./spreadsheet-editor.css";

const getRowIds = (rows, gridLength) =>
  Array.from({ length: gridLength }, (_, index) =>
    rows[index]?.id || rows[index]?._id || undefined,
  );

const getOriginalRows = (rows, gridLength) =>
  Array.from({ length: gridLength }, (_, index) => rows[index]);

const getValidationOptions = (originalRows, rowIds, gatheringID) => ({
  originalRows,
  rowIds,
  gatheringID,
});

const prepareGrid = (rows, gatheringID) => {
  const normalizedGrid = buildSpreadsheetGrid(rows);
  const rowIds = getRowIds(rows, normalizedGrid.length);
  const originalRows = getOriginalRows(rows, normalizedGrid.length);
  const errors = validateSpreadsheetGrid(
    normalizedGrid,
    getValidationOptions(originalRows, rowIds, gatheringID),
  );

  return {
    grid: annotateSpreadsheetGrid(normalizedGrid, errors),
    rowIds,
    originalRows,
    errors,
  };
};

const countChangedRows = (grid, originalRows, rowIds) =>
  grid.reduce((count, row, rowIndex) => {
    const originalRow = originalRows[rowIndex];
    const rowId = rowIds[rowIndex];

    if (isSpreadsheetRowBlank(row) && !rowId) return count;
    if (!originalRow) return count + 1;

    const changed = SPREADSHEET_COLUMNS.some(
      ({ key }, columnIndex) =>
        getSpreadsheetCellValue(row[columnIndex]) !== String(originalRow[key] ?? ""),
    );

    return count + (changed ? 1 : 0);
  }, 0);

export default function SpreadsheetEditor({
  isOpen,
  onClose,
  rows: initialRows = [],
  gatheringID,
  onSaveRows,
}) {
  const initialState = useMemo(
    () => prepareGrid(initialRows, gatheringID),
    // State is refreshed explicitly whenever the modal transitions from closed to open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [grid, setGrid] = useState(initialState.grid);
  const [rowIds, setRowIds] = useState(initialState.rowIds);
  const [originalRows, setOriginalRows] = useState(initialState.originalRows);
  const [cellErrors, setCellErrors] = useState(initialState.errors);
  const [isSaving, setIsSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const wasOpenRef = useRef(false);
  const spreadsheetRef = useRef(null);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const nextState = prepareGrid(initialRows, gatheringID);
      setGrid(nextState.grid);
      setRowIds(nextState.rowIds);
      setOriginalRows(nextState.originalRows);
      setCellErrors(nextState.errors);
      setSaveNotice(null);
      setIsSaving(false);
    }
    wasOpenRef.current = isOpen;
  }, [gatheringID, initialRows, isOpen]);

  const validationOptions = useMemo(
    () => getValidationOptions(originalRows, rowIds, gatheringID),
    [gatheringID, originalRows, rowIds],
  );

  const changedRowCount = useMemo(
    () => countChangedRows(grid, originalRows, rowIds),
    [grid, originalRows, rowIds],
  );
  const errorCount = Object.keys(cellErrors).length;

  const DataViewer = ({ cell }) => {
    const error = cell?.error;
    return (
      <span
        className={`ss-cell-view${error ? " ss-cell-view-error" : ""}`}
        title={error || undefined}
        aria-invalid={error ? "true" : undefined}
        aria-label={error ? `${getSpreadsheetCellValue(cell)}. ${error}` : undefined}
      >
        {getSpreadsheetCellValue(cell)}
      </span>
    );
  };

  const updateGrid = (nextGrid) => {
    const normalizedGrid = normalizeSpreadsheetGrid(nextGrid);
    const errors = validateSpreadsheetGrid(normalizedGrid, validationOptions);

    setGrid(annotateSpreadsheetGrid(normalizedGrid, errors));
    setCellErrors(errors);
    setSaveNotice(null);
  };

  const handleAddRow = () => {
    const firstBlankRow = grid.findIndex(
      (row, rowIndex) => isSpreadsheetRowBlank(row) && !rowIds[rowIndex],
    );
    const rowIndex = firstBlankRow >= 0 ? firstBlankRow : grid.length;

    if (firstBlankRow < 0) {
      updateGrid([
        ...grid,
        SPREADSHEET_COLUMNS.map(() => ({ value: "" })),
      ]);
    }

    window.requestAnimationFrame(() => {
      spreadsheetRef.current?.activate({ row: rowIndex, column: 0 });
    });
  };

  const requestClose = () => {
    if (isSaving) return;
    if (
      changedRowCount > 0 &&
      !window.confirm("Discard your unsaved spreadsheet changes?")
    ) {
      return;
    }
    onClose();
  };

  const applyPersistedRows = (persistedRows) => {
    const nextRowIds = [...rowIds];
    const nextOriginalRows = [...originalRows];
    const persistedByRow = new Map();

    persistedRows.forEach(({ rowIndex, event }) => {
      nextRowIds[rowIndex] = event.id;
      nextOriginalRows[rowIndex] = event;
      persistedByRow.set(rowIndex, event);
    });

    const normalizedGrid = normalizeSpreadsheetGrid(
      grid.map((row, rowIndex) => {
        const persistedEvent = persistedByRow.get(rowIndex);
        if (!persistedEvent) return row;
        return SPREADSHEET_COLUMNS.map(({ key }, columnIndex) => ({
          ...(row[columnIndex] || {}),
          value: persistedEvent[key] ?? "",
        }));
      }),
    );
    const nextOptions = getValidationOptions(
      nextOriginalRows,
      nextRowIds,
      gatheringID,
    );
    const nextErrors = validateSpreadsheetGrid(normalizedGrid, nextOptions);

    setRowIds(nextRowIds);
    setOriginalRows(nextOriginalRows);
    setGrid(annotateSpreadsheetGrid(normalizedGrid, nextErrors));
    setCellErrors(nextErrors);
  };

  const handleSave = async () => {
    const errors = validateSpreadsheetGrid(grid, validationOptions);
    if (Object.keys(errors).length > 0) {
      setCellErrors(errors);
      setGrid(annotateSpreadsheetGrid(grid, errors));
      setSaveNotice({
        type: "error",
        message: "Fix the highlighted cells before saving.",
      });
      return;
    }

    const changes = spreadsheetGridToChanges(grid, validationOptions);
    if (changes.length === 0) return;
    if (typeof onSaveRows !== "function") {
      setSaveNotice({ type: "error", message: "Schedule saving is unavailable." });
      return;
    }

    setIsSaving(true);
    setSaveNotice(null);

    try {
      const result = (await onSaveRows(changes)) || {};
      const persistedRows = result.persistedRows || [];
      const saveErrors = result.errors || [];

      if (persistedRows.length > 0) applyPersistedRows(persistedRows);

      if (saveErrors.length > 0) {
        setSaveNotice({
          type: "error",
          message: `${persistedRows.length} row${persistedRows.length === 1 ? "" : "s"} saved; ${saveErrors.length} could not be saved. Your remaining edits are still here.`,
        });
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to save spreadsheet rows", error);
      setSaveNotice({
        type: "error",
        message: error?.message || "The schedule could not be saved. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const status = errorCount > 0
    ? { tone: "error", icon: <FiAlertCircle />, text: `${errorCount} cell${errorCount === 1 ? " needs" : "s need"} attention` }
    : changedRowCount > 0
      ? { tone: "dirty", icon: <FiSave />, text: `${changedRowCount} unsaved row${changedRowCount === 1 ? "" : "s"}` }
      : { tone: "ok", icon: <FiCheck />, text: "Up to date" };

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      overlayClassName="spreadsheet-modal-overlay"
      contentClassName="fullscreen-spreadsheet-modal"
      style={{
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        padding: 0,
        borderRadius: 0,
        overflow: "hidden",
      }}
    >
      <div className="spreadsheet-editor-fullscreen">
        <header className="ss-header">
          <div className="ss-title-group">
            <span className="ss-eyebrow">Schedule workspace</span>
            <h2>Edit schedule</h2>
            <p className="ss-subtitle">
              Paste directly from Excel, Numbers, or Google Sheets. Changes are
              validated as you type.
            </p>
          </div>
          <div className="ss-actions">
            <div className={`ss-status ss-status-${status.tone}`} aria-live="polite">
              {status.icon}
              <span>{status.text}</span>
            </div>
            <button type="button" className="ss-button ss-button-secondary" onClick={requestClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              className="ss-button ss-button-primary"
              onClick={handleSave}
              disabled={isSaving || errorCount > 0 || changedRowCount === 0}
              title={errorCount > 0 ? "Fix highlighted cells before saving" : undefined}
            >
              <FiSave />
              {isSaving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </header>

        <div className="ss-toolbar">
          <button type="button" className="ss-add-row" onClick={handleAddRow} disabled={isSaving}>
            <FiPlus /> Add row
          </button>
          <span className="ss-toolbar-divider" />
          <p>
            <kbd>⌘ C</kbd> copy <span aria-hidden="true">·</span> <kbd>⌘ V</kbd> paste
            <span aria-hidden="true"> · </span> double-click a cell to edit
          </p>
        </div>

        {saveNotice && (
          <div className={`ss-notice ss-notice-${saveNotice.type}`} role="alert">
            <FiAlertCircle />
            <span>{saveNotice.message}</span>
          </div>
        )}

        <div className="ss-grid-wrap" aria-label="Schedule spreadsheet">
          <Spreadsheet
            ref={spreadsheetRef}
            className="ss-spreadsheet"
            data={grid}
            columnLabels={SPREADSHEET_COLUMNS.map(({ label }) => label)}
            onChange={updateGrid}
            DataViewer={DataViewer}
          />
        </div>

        <footer className="ss-footer-note">
          <span className="ss-error-swatch" aria-hidden="true" />
          Red cells block saving. Hover over one to see the exact validation message.
          Blank rows are ignored.
        </footer>
      </div>
    </Modal>
  );
}
